import { FolderOpen } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useSearchParams } from 'react-router'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export type ProjectSelectOption = {
  id: string
  displayName: string
}

type Props = {
  projects: ProjectSelectOption[]
  isOptional: boolean
  label?: string
}

export const COMPANY_LEVEL_VALUE = '__company_level__'

export const ProjectSelectField = ({
  projects,
  isOptional,
  label = 'Project',
}: Props) => {
  const form = useFormContext<{ idProject?: string }>()
  const [, setSearchParams] = useSearchParams()

  const handleValueChange = (value: string) => {
    const isCompanyLevel = value === COMPANY_LEVEL_VALUE

    // Store the actual value in the form (including COMPANY_LEVEL_VALUE)
    form.setValue('idProject', value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })

    // Update URL params (remove idProject when company level is selected)
    setSearchParams(
      (prev) => {
        if (!isCompanyLevel) {
          prev.set('idProject', value)
        } else {
          prev.delete('idProject')
        }
        return prev
      },
      { replace: true }
    )
  }

  return (
    <FormField
      control={form.control}
      name="idProject"
      render={({ field }) => {
        const isCompanyLevel = field.value === COMPANY_LEVEL_VALUE
        const displayProject = !isCompanyLevel
          ? projects.find((project) => project.id === field.value)
          : null

        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <Select onValueChange={handleValueChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      isOptional
                        ? 'Select a project (optional)'
                        : 'Select a project'
                    }
                  >
                    {isCompanyLevel ? (
                      <span className="text-muted-foreground">
                        None (Company level)
                      </span>
                    ) : displayProject ? (
                      <span className="flex items-center gap-2">
                        <FolderOpen className="size-4" aria-hidden="true" />
                        {displayProject.displayName}
                      </span>
                    ) : null}
                  </SelectValue>
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {isOptional ? (
                  <SelectItem value={COMPANY_LEVEL_VALUE}>
                    <span className="text-muted-foreground">
                      None (Company level)
                    </span>
                  </SelectItem>
                ) : null}
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    <span className="flex items-center gap-2">
                      <FolderOpen className="size-4" aria-hidden="true" />
                      {project.displayName}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
